import { ChangeEvent, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { InputField, Button, Input } from '@ets/design-system';

import styles from './EditableField.module.scss';
import { Textarea } from '_common/lib/components';

export type EditableFieldProps<I extends string = string> = {
  id: I;
  type: 'input' | 'textarea';
  isEditing: boolean;
  value?: string;
  placeholder: string;
  maxChars: number;
  errorLabels: {
    specialChars?: string;
    maxChars?: string;
  };
  onSave: (newValue?: string) => void;
  onEditing: (payload: { id: I; isEditing: boolean }) => void;
  testId: string;
};

const EditableField = <I extends string = string>({
  id,
  type,
  isEditing,
  value,
  placeholder,
  maxChars,
  errorLabels,
  onSave,
  onEditing,
  testId,
}: EditableFieldProps<I>) => {
  const [localValue, setLocalValue] = useState(value);
  const [editing, setEditing] = useState(isEditing);
  const [feedback, setFeedback] = useState<string>();

  useEffect(() => {
    setEditing(isEditing);
  }, [isEditing]);

  useEffect(() => {
    onEditing({ isEditing: editing, id });
  }, [editing, id]);

  const handleStartEdit = () => {
    setEditing(true);
  };

  const handleStopEdit = () => {
    setEditing(false);
  };

  const handleSave = () => {
    onSave(localValue);
    setEditing(false);
  };

  const handleValueChange = (newValue: string) => {
    setLocalValue(newValue);

    if (errorLabels.maxChars && newValue.length > maxChars) {
      setFeedback(errorLabels.maxChars);
    } else if (errorLabels.specialChars && /[<>/\\:?*"]/.test(newValue)) {
      setFeedback(errorLabels.specialChars);
    } else {
      setFeedback(undefined);
    }
  };

  const handleOnChange = (e: ChangeEvent) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    handleValueChange(target.value);
  };

  const renderField = () => {
    switch (type) {
      case 'input':
        return (
          <Input
            size="l"
            value={localValue}
            onChange={handleOnChange}
            placeholder={placeholder}
            testId={`${testId}-field`}
          />
        );
      case 'textarea':
        return (
          <Textarea
            plainText
            name="editable-field"
            size="m"
            type="text"
            initialContent={localValue}
            onChange={handleOnChange}
            placeholder={placeholder}
            testId={`${testId}-field`}
          />
        );
    }
  };

  if (editing) {
    return (
      <div className={styles.root}>
        <div className={styles.content}>
          <InputField feedbackContent={feedback} testId={testId} label="" size="m">
            {renderField()}
          </InputField>
          <div className={styles.buttons}>
            <Button
              size="s"
              variant="standard"
              onClick={handleStopEdit}
              testId={`${testId}-cancel`}
            >
              <FormattedMessage id="global.cancel" />
            </Button>
            <Button
              size="s"
              variant="primary"
              onClick={handleSave}
              disabled={localValue === value || !!feedback}
              testId={`${testId}-save`}
            >
              <FormattedMessage id="global.save" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <div className={styles.label}>{value}</div>
      </div>
      <Button
        size="m"
        variant="link"
        icon="pen-to-square"
        onClick={handleStartEdit}
        testId={`${testId}-edit`}
      />
    </div>
  );
};

export default EditableField;
